<template>
  <b-modal v-model="active"
           title="Precios de venta para productos seleccionados"
           centered
           scrollable
           size="lg"
           :ok-title="saving ? 'Guardando...' : 'Aplicar precios'"
           cancel-title="Cancelar"
           :ok-disabled="!hasChanges || !isValidPurchasePrice || saving"
           :cancel-disabled="saving"
           @ok.prevent="confirmSave">
    <div v-if="loading" class="text-center my-4">
      <b-spinner class="mr-2" small /> Cargando configuración de precios...
    </div>
    <template v-else>
      <b-card no-body class="mb-3 ppbe-selection-card">
        <b-card-header class="p-0">
          <b-button block variant="link" class="text-left ppbe-products-toggle" @click="productsOpen = !productsOpen">
            <AppIcon :name="productsOpen ? 'expand_less' : 'expand_more'" />
            <strong>{{ items.length }} productos que se modificarán</strong>
            <span class="ppbe-selection-card__hint">Mismo precio final; utilidad calculada por producto</span>
          </b-button>
        </b-card-header>
        <b-collapse :visible="productsOpen">
          <ul class="list-group list-group-flush">
            <li v-for="item in items" :key="item.selectProduct.id" class="list-group-item py-2 small">
              <strong>{{ item.product.code }}</strong> — {{ item.product.name }}
              <span class="float-right">Costo actual: {{ formatCurrency(item.price_cost) }}</span>
            </li>
          </ul>
        </b-collapse>
      </b-card>

      <b-card no-body class="mb-3 ppbe-panel">
        <b-card-header class="ppbe-panel__header"><strong><AppIcon name="cart" /> Costo de compra</strong></b-card-header>
        <b-card-body class="py-2">
          <b-row>
            <b-col md="5">
              <label class="small font-weight-bold mb-1">Precio de compra a aplicar</label>
              <app-number-input v-model="purchasePrice" size="sm" :decimals="2" placeholder="Obligatorio" @input="emitPurchasePrice" />
              <small class="text-muted">Se aplicará el mismo precio de compra a todos los productos.</small>
            </b-col>
            <b-col md="7" class="pt-2 pt-md-0">
              <div class="small text-muted mb-1">Desglose compartido de compra (solo lectura)</div>
              <div class="ppbe-breakdown">{{ purchaseBreakdown }}</div>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <b-card no-body class="mb-3 ppbe-panel">
        <b-card-header class="ppbe-panel__header d-flex align-items-center">
          <strong><AppIcon name="tags" /> Listas de venta</strong>
          <b-form-checkbox v-model="priceWithIva" switch class="mb-0 ml-auto" @change="onPriceWithIvaChange">c/IVA</b-form-checkbox>
        </b-card-header>
        <b-card-body class="py-2">

      <div v-if="!lists.length" class="text-muted">Los productos seleccionados no tienen listas de venta asignadas.</div>
      <div v-for="list in lists" :key="list.price_list_id" class="ppbe-list-row">
        <b-form-checkbox v-model="list.apply"
                         class="mr-2"
                         :disabled="!list.assigned"
                         v-b-tooltip.hover.right
                         :title="list.assigned ? 'Lista asignada a los productos seleccionados' : 'Esta lista no está asignada a los productos seleccionados'"
                         :aria-label="'Modificar ' + list.name" />
        <div class="ppbe-list-row__name">
          <strong>{{ list.name }}</strong>
          <small v-if="list.assigned">
            Descuento: {{ list.desc }}% · {{ listUtilityLabel(list) }}
            <span v-if="list.suggestionReason" class="text-warning"> · {{ list.suggestionReason }}</span>
          </small>
          <small v-else>Sin asignar a los productos seleccionados</small>
        </div>
        <app-number-input v-model="list.sale_price" size="sm" :decimals="2" :disabled="!list.apply" :placeholder="list.suggestionUnavailable ? 'Sin sugerencia' : ''" class="ppbe-list-row__input" @input="markManualPrice(list)" />
        <span class="text-muted small">{{ priceWithIva ? 'c/IVA' : 's/IVA' }}</span>
      </div>
      <small class="text-muted d-block mt-2">Ingresá el precio de venta que querés para cada lista. El sistema ajustará la utilidad de cada producto para llegar a ese importe.</small>
        </b-card-body>
      </b-card>

    </template>
  </b-modal>
</template>

<script>
  import costsServiceAPI from './services'
  import ErrorHandler from '@/handler/error'
  import { roundMoney, toNumber, utilityFromSalePrice } from './pricingMath'

  export default {
    name: 'ProductPricingBulkEditor',
    props: {
      currency: { type: String, default: 'ARS' },
      savePricing: { type: Function, required: true },
    },
    data() {
      return {
        active: false,
        loading: false,
        saving: false,
        items: [],
        lists: [],
        priceWithIva: true,
        previousPriceWithIva: true,
        ivaConditions: [],
        purchasePrice: '',
        productsOpen: false,
      }
    },
    computed: {
      ivaAliquot() {
        var product = this.items.length && this.items[0].selectProduct ? this.items[0].selectProduct : {}
        var ivaId = product.erp_iva_condition_id || product.iva_condition || 0
        var condition = this.ivaConditions.find(item => item.id == ivaId)
        return condition ? toNumber(condition.aliquot || condition.percentage) : 0
      },
      hasChanges() {
        return this.lists.some(list => list.apply && toNumber(list.sale_price) > 0)
      },
      isValidPurchasePrice() {
        return toNumber(this.purchasePrice) > 0
      },
      purchaseBreakdown() {
        var product = this.items.length && this.items[0].selectProduct ? this.items[0].selectProduct : {}
        var purchase = product.product_purchase || {}
        var values = []
        if(toNumber(purchase.additional)) values.push('Adic. inicial $' + purchase.additional)
        if(toNumber(purchase.percentage_discount)) values.push('Dto. 1 ' + purchase.percentage_discount + '%')
        if(toNumber(purchase.percentage_discount_1)) values.push('Dto. 2 ' + purchase.percentage_discount_1 + '%')
        if(toNumber(purchase.percentage_discount_2)) values.push('Dto. 3 ' + purchase.percentage_discount_2 + '%')
        if(toNumber(purchase.additional_percentage)) values.push('Adic. 1 ' + purchase.additional_percentage + '%')
        if(toNumber(purchase.additional_percentage_1)) values.push('Adic. 2 ' + purchase.additional_percentage_1 + '%')
        if(toNumber(purchase.additional_1)) values.push('Adic. final $' + purchase.additional_1)
        return values.length ? values.join(' · ') : 'Sin descuentos ni adicionales'
      },
    },
    watch: {
      purchasePrice() {
        this.refreshSuggestedPrices()
      },
    },
    methods: {
      open({ items = [], purchasePrice = '' } = {}) {
        this.items = items.slice()
        this.lists = []
        this.purchasePrice = purchasePrice
        this.productsOpen = false
        this.previousPriceWithIva = this.priceWithIva
        this.active = true
        this.loading = true
        this.saving = false
        costsServiceAPI.obtenerOpcionesProductoCostosPorCodigoBarras()
          .then(response => {
            var options = response.data || {}
            return costsServiceAPI.obtenerCondicionesIva().then(ivaResponse => {
              this.ivaConditions = Array.isArray(ivaResponse.data) ? ivaResponse.data : []
              this.buildLists(options.price_lists || [])
              this.loading = false
            })
          })
          .catch(error => {
            this.loading = false
            this.$awn.alert(ErrorHandler.showError(error))
          })
      },
      buildLists(options) {
        var product = this.items.length && this.items[0].selectProduct ? this.items[0].selectProduct : {}
        var prices = Array.isArray(product.prices) ? product.prices : []
        this.lists = options.map(option => {
          var price = prices.find(item => item.price_list_id == option.id)
          return {
            price_list_id: option.id,
            name: option.name || ('Lista #' + option.id),
            desc: toNumber(price ? price.desc : 0),
            assigned: !!price,
            apply: !!price,
            sale_price: '',
            default_utility: toNumber(option.utility),
            suggestionUnavailable: false,
            suggestionReason: '',
            manualPrice: false,
          }
        })
        this.refreshSuggestedPrices()
      },
      getItemPrice(item, listID) {
        var product = item && item.selectProduct ? item.selectProduct : {}
        var prices = Array.isArray(product.prices) ? product.prices : []
        return prices.find(price => price.price_list_id == listID)
      },
      getFinalSalePrice(list) {
        var value = toNumber(list.sale_price)
        if(!this.priceWithIva) return value * (1 + this.ivaAliquot / 100)
        return value
      },
      getEffectiveUtility(item, list) {
        var price = this.getItemPrice(item, list.price_list_id)
        var utility = price ? toNumber(price.utility) : 0
        return utility > 0 ? utility : toNumber(list.default_utility)
      },
      getSuggestedSalePrice(list) {
        if(!list.assigned || !this.isValidPurchasePrice) return null
        var utilities = this.items.map(item => this.getEffectiveUtility(item, list))
        var unique = utilities.filter((value, index) => utilities.indexOf(value) == index)
        if(unique.length != 1) return null
        var cost = this.getCostForCalculation(this.items[0])
        var netPrice = cost * (1 + unique[0] / 100) * (1 - toNumber(list.desc) / 100)
        var finalPrice = this.priceWithIva ? netPrice * (1 + this.ivaAliquot / 100) : netPrice
        return roundMoney(finalPrice)
      },
      getSuggestionReason(list) {
        if(!list.assigned) return ''
        if(!this.isValidPurchasePrice) return 'Ingresá un precio de lista para calcular la sugerencia'
        var utilities = this.items.map(item => this.getEffectiveUtility(item, list))
        var unique = utilities.filter((value, index) => utilities.indexOf(value) == index)
        if(unique.length != 1) return 'No se sugiere un precio: las utilidades actuales son distintas'
        if(!(this.getCostForCalculation(this.items[0]) > 0)) return 'No se pudo calcular el costo resultante'
        return 'No se pudo generar una sugerencia'
      },
      refreshSuggestedPrices() {
        this.lists.forEach(list => {
          if(list.manualPrice) return
          var suggested = this.getSuggestedSalePrice(list)
          list.suggestionUnavailable = list.assigned && !(suggested > 0)
          list.suggestionReason = list.suggestionUnavailable ? this.getSuggestionReason(list) : ''
          list.sale_price = suggested > 0 ? suggested : 0
        })
      },
      markManualPrice(list) {
        list.manualPrice = true
        list.suggestionUnavailable = false
        list.suggestionReason = ''
      },
      onPriceWithIvaChange() {
        var oldValue = this.previousPriceWithIva
        var newValue = this.priceWithIva
        var ivaFactor = 1 + this.ivaAliquot / 100
        this.lists.forEach(list => {
          if(list.manualPrice && toNumber(list.sale_price) > 0 && ivaFactor > 0) {
            list.sale_price = roundMoney(oldValue && !newValue
              ? toNumber(list.sale_price) / ivaFactor
              : (!oldValue && newValue ? toNumber(list.sale_price) * ivaFactor : list.sale_price))
          }
        })
        this.previousPriceWithIva = newValue
        this.refreshSuggestedPrices()
      },
      listUtilityLabel(list) {
        var values = this.items.map(item => {
          var price = this.getItemPrice(item, list.price_list_id)
          return price ? toNumber(price.utility) : 0
        })
        var unique = values.filter((value, index) => values.indexOf(value) == index)
        return unique.length == 1 ? 'Utilidad actual: ' + unique[0] + '%' : 'La utilidad actual varía por producto'
      },
      getCostForCalculation() {
        return toNumber(this.purchasePrice)
      },
      emitPurchasePrice() {
        this.$emit('purchase-price-change', this.purchasePrice)
      },
      confirmSave() {
        if(!this.hasChanges || !this.isValidPurchasePrice || this.saving) return
        var listCount = this.lists.filter(list => list.apply && toNumber(list.sale_price) > 0).length
        this.$bvModal.msgBoxConfirm(
          'Se aplicará el precio de lista y se actualizarán ' + listCount + (listCount == 1 ? ' lista de venta' : ' listas de venta') + ' para ' + this.items.length + (this.items.length == 1 ? ' producto.' : ' productos.'),
          { title: 'Confirmar actualización de precios', okTitle: 'Aplicar precios', cancelTitle: 'Cancelar', okVariant: 'primary', centered: true }
        ).then(confirmed => {
          if(confirmed) this.save()
        })
      },
      save() {
        var changes = this.lists.filter(list => list.apply && toNumber(list.sale_price) > 0)
        if(!changes.length || !this.isValidPurchasePrice || this.saving) return

        var operations = []
        for(var i = 0; i < this.items.length; i++) {
          for(var j = 0; j < changes.length; j++) {
            var item = this.items[i]
            var list = changes[j]
            var price = this.getItemPrice(item, list.price_list_id)
            var utility = utilityFromSalePrice({
              cost: this.getCostForCalculation(item),
              salePrice: this.getFinalSalePrice(list),
              discount: price ? price.desc : list.desc,
              ivaAliquot: this.ivaAliquot,
            })
            if(!price || !price.id || utility === null) {
              this.$awn.alert('No se pudo calcular la utilidad para todos los productos seleccionados')
              return
            }
            operations.push({ item, price, utility })
          }
        }

        this.saving = true
        this.savePricing({ items: this.items, operations: operations, purchasePrice: this.purchasePrice })
          .then(() => {
            this.saving = false
            this.active = false
            this.$emit('purchase-price-change', '')
            this.$awn.success('Precios de venta actualizados')
            this.$emit('prices-saved', this.items)
          })
          .catch(error => {
            this.saving = false
            this.$awn.alert(ErrorHandler.showError(error))
          })
      },
      formatCurrency(value) {
        return Intl.NumberFormat('es-AR', { style: 'currency', currency: this.currency }).format(toNumber(value))
      },
    },
  }
</script>

<style scoped>
  .ppbe-list-row { display: flex; align-items: center; gap: 8px; padding: 9px 0; border-bottom: 1px solid #e9ecef; }
  .ppbe-list-row__name { flex: 1; min-width: 140px; display: flex; flex-direction: column; }
  .ppbe-list-row__name small { color: #6c757d; }
  .ppbe-list-row__input { width: 150px; }
  .ppbe-panel { border-color: #d7e3eb; }
  .ppbe-selection-card { border-color: #b7d7e6; background: #eef9fd; }
  .ppbe-selection-card__hint { display: block; margin-left: 25px; font-size: 0.76rem; color: #527184; font-weight: normal; }
  .ppbe-panel__header { background: #f7fafc; color: #41566b; font-size: 0.82rem; }
  .ppbe-breakdown { font-size: 0.78rem; color: #52687c; line-height: 1.6; }
  .ppbe-products-toggle { color: #41566b; font-size: 0.84rem; text-decoration: none; }
  @media (max-width: 575.98px) { .ppbe-list-row { flex-wrap: wrap; } .ppbe-list-row__input { width: 130px; } }
</style>
